import { useCallback } from "react";
import { graphql, useMutation } from "react-relay";

import type {
  enableOrganizationUsersMutation,
  organizationpermission,
} from "__generated__/enableOrganizationUsersMutation.graphql";

export const EnableOrganizationUsersMutation = graphql`
  mutation enableOrganizationUsersMutation(
    $organizationId: ID!
    $emails: [String!]!
    $organizationPermission: organizationpermission
    $resourceGroupLinks: [ResourceGroupLinkInput]
    $expiry: DateTime
    $connections: [ID!]!
  ) {
    bulkEnableOrganizationUsers(
      organizationId: $organizationId
      emails: $emails
      organizationPermission: $organizationPermission
      resourceGroupLinks: $resourceGroupLinks
      expiry: $expiry
    ) {
      enabledUsers @appendNode(connections: $connections, edgeTypeName: "OrganizationUserEdge") {
        id
        email
        uuid
        email
        displayName
        firstName
        lastName
        organizationPermission
        resourceGroupLinks {
          edges {
            node {
              id
              resourceGroupId
              resourceGroupName
              resourceGroupPermission
            }
          }
        }
        supportedLoginOptions {
          loginType
          identityProviderConfiguration {
            name
            providerType
          }
        }
        ...UserProfile_loginTypes
        loginTypesOverriden
        loginTypes {
          edges {
            node {
              loginType
              authEnabled
              identityProviderConfiguration {
                id
                name
                providerType
                uuid
              }
            }
          }
        }
        expiry
      }
      errors
    }
  }
`;

interface ResourceGroupLinkInput {
  resourceGroupNodeId: string;
  resourceGroupPermission: organizationpermission;
}

export const useEnableOrganizationUsers = (
  onCompleted?: (response: enableOrganizationUsersMutation["response"]) => void
) => {
  const [enabledUsers, loading] = useMutation<enableOrganizationUsersMutation>(
    EnableOrganizationUsersMutation
  );

  return {
    enableUsers: useCallback(
      (
        organizationId: string,
        emails: string[],
        organizationPermission: organizationpermission,
        resourceGroupLinks: ResourceGroupLinkInput[],
        expiry: string | null,
        connections: string[]
      ) => {
        enabledUsers({
          variables: {
            organizationId,
            emails,
            organizationPermission,
            resourceGroupLinks,
            expiry,
            connections,
          },
          onCompleted,
        });
      },
      [enabledUsers, onCompleted]
    ),
    loading,
  };
};
